/**
 * Proposal domain contracts — Module 3 (TW-3443), the operator builder.
 *
 * TC builds a priced proposal for a trip: line items each carrying a per-line margin (a % markup
 * on net, or a fixed € handling fee per unit) — accommodation options are just line items (TW UAT
 * round 1) — plus a covering message. As elsewhere, the Zod schemas here are the single source of truth
 * for request validation — the API validates the saved Draft with them and the web builder validates
 * against the same objects (architecture §4.1).
 *
 * The money maths lives here as pure, deterministic functions so the summary rail in the web builder
 * and the persisted `unit_price`/`amount`/`total_amount` derived on the server use one identical
 * implementation (web has no test runner, so these are proven in the API vitest harness). They
 * compute in plain number space with explicit whole-euro rounding — exactly the prototype's
 * behaviour — and the server persists the results into the Decimal columns; no Decimal library is
 * introduced (data-model §5).
 */
import { z } from "zod";
import { type ProposalStatus, type TripStatus } from "./constants";

// ── Per-line margin ───────────────────────────────────────────────────────────

/** How a line's margin is applied: a percentage markup on net, or a fixed € fee per unit. */
export const MARGIN_TYPES = ["pct", "fixed"] as const;
export type MarginType = (typeof MARGIN_TYPES)[number];

/**
 * Line categories the builder offers. Drives the default margin on a new line and groups the editor;
 * the chosen key is now also *persisted* on `proposal_line_item.category` (TW UAT round 1) so the
 * club's option list renders the same groups. Kept here so web and server agree on the set.
 */
export const PROPOSAL_CATEGORIES = [
  "cat_transport",
  "cat_hotel",
  "cat_transfers",
  "cat_baggage",
  "cat_extras",
  "cat_other",
] as const;
export type ProposalCategory = (typeof PROPOSAL_CATEGORIES)[number];

/** Default % markup per category for a freshly added line (ported from the prototype's CAT_MARGIN).
 *  Transport is the exception: it seeds a fixed € fee ({@link FIXED_FEE_TRANSPORT}), not a %. */
export const CATEGORY_DEFAULT_MARGIN: Record<ProposalCategory, number> = {
  cat_transport: 8,
  cat_hotel: 12,
  cat_transfers: 15,
  cat_baggage: 20,
  cat_extras: 18,
  cat_other: 15,
};

/** Fixed € handling fee per ticket seeded for a transport line (TW UAT round 3: €9 across modes). */
export const FIXED_FEE_TRANSPORT = 9;

// ── Margin maths (pure, the single source for web + server) ─────────────────────

/** The economics needed to price one line — a subset shared by the editor state and the DTO. */
export interface ProposalLineMath {
  qty: number;
  unitCost: number;
  marginType: MarginType;
  marginValue: number;
}

const num = (v: number) => (Number.isFinite(v) ? v : 0);

/** Net (cost) total for a line: qty × unit cost. */
export function lineNet(line: ProposalLineMath): number {
  return num(line.qty) * num(line.unitCost);
}

/** Margin amount for a line: a fixed fee per unit, or a percentage of the line's net cost. */
export function lineMarginAmount(line: ProposalLineMath): number {
  return line.marginType === "fixed"
    ? num(line.qty) * num(line.marginValue)
    : (lineNet(line) * num(line.marginValue)) / 100;
}

/** Client (sell) total for a line, rounded to whole euros: net + margin. */
export function lineClientTotal(line: ProposalLineMath): number {
  return Math.round(lineNet(line) + lineMarginAmount(line));
}

/** Derived client price per unit (for the `unit_price` column); falls back to the line total. */
export function lineUnitPrice(line: ProposalLineMath): number {
  const qty = num(line.qty);
  const total = lineClientTotal(line);
  return qty > 0 ? Math.round((total / qty) * 100) / 100 : total;
}

/** The live summary rail figures — net cost, margin €, client total, blended % and per-pax. */
export interface ProposalTotals {
  /** Σ line net cost. */
  netCost: number;
  /** Σ client total. */
  total: number;
  /** total − netCost. */
  marginAmount: number;
  /** Blended margin %: ((total / netCost) − 1) × 100, whole-euro rounded; 0 when netCost is 0. */
  blended: number;
  /** Client total ÷ pax, whole euros; 0 when there are no travellers. */
  perPerson: number;
}

/** Fold a set of lines (and the trip's pax) into the summary figures, matching the prototype. */
export function computeProposalTotals(
  lines: readonly ProposalLineMath[],
  pax: number | null | undefined,
): ProposalTotals {
  const netCost = lines.reduce((s, l) => s + lineNet(l), 0);
  const total = lines.reduce((s, l) => s + lineClientTotal(l), 0);
  const blended = netCost > 0 ? Math.round((total / netCost - 1) * 100) : 0;
  const perPerson = pax && pax > 0 ? Math.round(total / pax) : 0;
  return { netCost, total, marginAmount: total - netCost, blended, perPerson };
}

// Hotel confirmation was removed in favour of accommodation line items (TW UAT round 1): a proposal
// no longer needs a "confirmed hotel" to be sent, so the former send gate (`proposalSendBlock`) is
// gone. A Draft with at least one line is sendable; the workflow rules live in the service.

// ── Adaptive deadline setup (TW-3444; reworked TW UAT round 2) ───────────────────

/**
 * The proposal's three standard deadline columns (data-model §5). Each is a nullable `timestamptz`
 * on the `proposal` row: a deadline toggled *off* in the builder is persisted as `null`, toggled
 * *on* as the chosen date. These are the fixed, transport-driven deadlines; TC can also add
 * free-form {@link ProposalCustomDeadlineDto} rows on top (stored in `proposal_custom_deadline`).
 */
export const PROPOSAL_DEADLINE_KEYS = [
  "acceptanceDeadline",
  "passengerListDeadline",
  "trainProposalDeadline",
] as const;
export type ProposalDeadlineKey = (typeof PROPOSAL_DEADLINE_KEYS)[number];

/**
 * Whether a deadline always applies (to every trip) or only when the trip requested train travel
 * specifically. (Acceptance + passenger-list are universal — every trip is quoted and files a
 * manifest; only the train-proposal release is mode-specific.)
 */
export type DeadlineRequires = "always" | "train";

/** A deadline's static metadata — its column, its i18n label key and its seed offset. */
export interface ProposalDeadlineDef {
  /** The `proposal` column / DTO field this deadline maps to. */
  key: ProposalDeadlineKey;
  /** i18n key for the row label (the `proposal.dl*` strings). */
  labelKey: string;
  /** Default number of days from "now" the builder seeds this deadline at. */
  defaultOffsetDays: number;
  /** Whether the row always shows, or only for a transport / train trip. */
  requires: DeadlineRequires;
}

/**
 * The standard deadline set in display order (reworked TW UAT round 2): proposal-acceptance and the
 * passenger-list deadline apply to every trip (every trip is quoted and files a manifest); the
 * train-proposal acceptance only for a train trip. The default offsets seed the date inputs; the
 * operator can re-date, toggle each off, or add custom deadlines alongside.
 */
export const PROPOSAL_DEADLINE_DEFS: readonly ProposalDeadlineDef[] = [
  { key: "acceptanceDeadline", labelKey: "proposal.dlProposal", defaultOffsetDays: 5, requires: "always" },
  { key: "passengerListDeadline", labelKey: "proposal.dlPaxList", defaultOffsetDays: 21, requires: "always" },
  { key: "trainProposalDeadline", labelKey: "proposal.dlTrainProposal", defaultOffsetDays: 13, requires: "train" },
];

/** The transport modes a trip requested — the adaptive deadline rows key off these. */
export interface DeadlineTransport {
  flight?: boolean;
  train?: boolean;
  coach?: boolean;
}

/**
 * The standard deadlines that apply to a trip given its requested transport modes — a pure,
 * deterministic function so the web builder renders exactly the rows the server expects to persist
 * (web has no test runner, so this is proven in the API vitest harness). Acceptance and the
 * passenger-list deadline are universal; the train-proposal row only shows for a train trip.
 */
export function applicableDeadlines(transport: DeadlineTransport | null | undefined): readonly ProposalDeadlineDef[] {
  return PROPOSAL_DEADLINE_DEFS.filter(
    (d) => d.requires === "always" || (d.requires === "train" && Boolean(transport?.train)),
  );
}

/** A free-form deadline TC adds to a proposal (TW UAT round 2): a label + a date, no transport rule. */
export interface ProposalCustomDeadlineDto {
  id: string;
  /** The operator-entered title, e.g. "Final balance due". Shown verbatim (not localized). */
  label: string;
  /** The deadline date (ISO `YYYY-MM-DD`). */
  date: string;
}

// ── Club review / accept (TW-3445; reworked TW UAT round 1) ─────────────────────

/**
 * Whether a proposal's acceptance deadline has lapsed — the deadline DAY is now in the past
 * (day-granular, matching the ops radar's "overdue" and the UI's "Overdue" label: due *today* has
 * not lapsed yet). A null/absent deadline never lapses. `nowIso` is passed in (clock-free) so the
 * result stays deterministic + testable. Once lapsed, the trip must auto-expire (the worker expiry
 * job / sweep transitions proposal + trip → `Expired`) and the club may no longer accept.
 */
export function isAcceptanceDeadlineLapsed(
  acceptanceDeadline: string | null | undefined,
  nowIso: string,
): boolean {
  if (!acceptanceDeadline) return false;
  return startOfDayMs(acceptanceDeadline) < startOfDayMs(nowIso);
}

/**
 * Whether the club may still accept this proposal — only a `Sent` proposal whose acceptance deadline
 * has not lapsed is acceptable. Pass the deadline + `nowIso` to also reject a lapsed proposal (the
 * server enforces this in `accept()`; the deadline arg is optional for callers that only know status).
 */
export function canAcceptProposal(
  status: ProposalStatus,
  opts?: { acceptanceDeadline?: string | null; nowIso?: string },
): boolean {
  if (status !== "Sent") return false;
  if (opts?.nowIso && isAcceptanceDeadlineLapsed(opts.acceptanceDeadline, opts.nowIso)) return false;
  return true;
}

/**
 * The club's acceptance is now a two-part commitment (TW UAT round 1): it picks the line items it
 * wants (row-level checkboxes) and, when a hotel is involved, authorises TC to pay a deposit to
 * reserve it. Both travel in the accept request. The unchosen lines are NOT deleted — they are
 * flagged `selected = false` and surface as `ProposalDto.unselectedLineItems` so both audiences can
 * still review the declined options (TRAV-66); `lineItems` carries the agreed (selected) scope.
 */
export const acceptProposalSchema = z.object({
  /** Ids of the line items the club chose to keep — must all belong to the live proposal. */
  selectedLineItemIds: z.array(z.uuid()).max(100),
  /** Whether the club authorises TC to pay a deposit to reserve the chosen accommodation. */
  depositAuthorized: z.boolean(),
});
export type AcceptProposalInput = z.infer<typeof acceptProposalSchema>;

/** Why the club cannot accept yet, or null when it can. */
export type ProposalAcceptBlock = "needsSelection" | "needsDepositAuth" | null;

/**
 * Whether the club may accept now, and if not, the reason key the UI surfaces. The Accept button is
 * disabled until this clears; the server re-checks the same gate (one rule, two enforcement points,
 * architecture §4.1). The club must keep at least one line item, and — when the trip has an overnight
 * stay (`requiresDeposit`, derived from the travel nights) — must authorise the hotel deposit.
 */
export function proposalAcceptBlock(input: {
  selectedCount: number;
  requiresDeposit: boolean;
  depositAuthorized: boolean;
}): ProposalAcceptBlock {
  if (input.selectedCount < 1) return "needsSelection";
  if (input.requiresDeposit && !input.depositAuthorized) return "needsDepositAuth";
  return null;
}

/** Whether the club may accept now (the gate clears). */
export function canAcceptProposalNow(input: {
  selectedCount: number;
  requiresDeposit: boolean;
  depositAuthorized: boolean;
}): boolean {
  return proposalAcceptBlock(input) === null;
}

// ── Back-office decision on behalf of the club (TRAV-74) ──────────────────────
//
// The club sometimes agrees a proposal with TC by phone/email but never clicks Accept — or never
// answers an amendment at all, so the trip lapses to Expired even though it went ahead. Back office
// can then record the club's decision for it: **accept** the live proposal (Sent, or lapsed to
// Expired — the acceptance deadline does not bind TC), or **reject** an unanswered amendment, which
// restores the previously accepted version and returns the trip to `Accepted`. Either way the
// operator states why (a predefined reason + optional note), recorded on the audit trail.

/** Why back office is recording the club's decision for it. `other` requires a note. */
export const ON_BEHALF_REASONS = ["phone", "email", "in_person", "other"] as const;
export type OnBehalfReason = (typeof ON_BEHALF_REASONS)[number];

const onBehalfReasonFields = {
  reason: z.enum(ON_BEHALF_REASONS),
  /** Free-text context, e.g. "Spoke to Marta on 12 Oct" — required when `reason` is `other`. */
  note: z.string().trim().max(1000).optional(),
};

const noteRequiredForOther = (v: { reason: OnBehalfReason; note?: string }) =>
  v.reason !== "other" || (v.note?.length ?? 0) > 0;
const noteRequiredIssue = { message: "A note is required when the reason is Other", path: ["note"] };

/** Accept the live proposal on the club's behalf — the club's own payload plus the reason. */
export const acceptOnBehalfSchema = acceptProposalSchema
  .extend(onBehalfReasonFields)
  .refine(noteRequiredForOther, noteRequiredIssue);
export type AcceptOnBehalfInput = z.infer<typeof acceptOnBehalfSchema>;

/** Reject the unanswered amendment on the club's behalf, restoring the previously accepted version. */
export const rejectOnBehalfSchema = z.object(onBehalfReasonFields).refine(noteRequiredForOther, noteRequiredIssue);
export type RejectOnBehalfInput = z.infer<typeof rejectOnBehalfSchema>;

/** The previously accepted version a reject-on-behalf would restore. */
export interface RestorableProposalDto {
  version: number;
  acceptedAt: string;
  totalAmount: number | null;
  currency: string;
}

/**
 * What the back-office "Record club decision" dialog needs (`GET …/proposal/club-decision`): the
 * live proposal the club is (or was) answering, whether it can be accepted for the club (and why
 * not), the version a reject would restore (null → reject unavailable), and whether the club will
 * be notified (only while the trip hasn't finished — see {@link shouldNotifyClubOfOnBehalf}).
 */
export interface ClubDecisionDto {
  live: ProposalDto | null;
  /** Why accepting for the club is blocked, or null when it is possible. */
  acceptBlock: "noLiveProposal" | "notAwaitingClub" | "amendedPending" | null;
  restorable: RestorableProposalDto | null;
  notifyClub: boolean;
}

/**
 * Whether a decision recorded on the club's behalf is announced to the club (TRAV-74): yes while the
 * trip hasn't finished — its end date (else start date) is today or later, or it has no dates — and
 * silently once it is already in the past (back office is just tidying up a trip that took place).
 * Day-granular, ISO string compare; `todayIso` is passed in so this stays pure.
 */
export function shouldNotifyClubOfOnBehalf(
  trip: { travelStartDate: string | null; travelEndDate: string | null },
  todayIso: string,
): boolean {
  const last = trip.travelEndDate ?? trip.travelStartDate;
  if (!last) return true;
  return last.slice(0, 10) >= todayIso.slice(0, 10);
}

/** The proposal fields the club checklist derives from (a subset of the DTO). */
export type ProposalChecklistSource = Pick<ProposalDto, "status" | "acceptedAt" | ProposalDeadlineKey> & {
  /** The free-form deadlines TC added (TW UAT round 2); optional for shape stability. */
  customDeadlines?: readonly ProposalCustomDeadlineDto[];
};

/**
 * Trip-level facts that satisfy deadlines beyond acceptance. Passed in (not on the proposal) since
 * they live on the trip: the manifest submission stamp meets the passenger-list deadline, and a
 * completed trip is a closed-out historical record where every deadline reads as met.
 */
export interface ProposalChecklistContext {
  /** The trip's manifest has been submitted — satisfies the passenger-list deadline. */
  manifestSubmitted?: boolean;
  /** The trip is completed (closed out) — every deadline reads as done on the record. */
  tripCompleted?: boolean;
}

/** One row of the club's deadline checklist (timeline) — what to render and whether it's actioned. */
export interface ProposalChecklistItem {
  /** A stable row key — the deadline column for a standard row, or `custom:<id>` for a custom one. */
  key: string;
  /** i18n key for the row label (a `proposal.dl*` string) for a standard row; null for a custom row. */
  labelKey: string | null;
  /** The literal, operator-entered label for a custom row (shown verbatim); null for a standard row. */
  label: string | null;
  /** The deadline date (ISO `YYYY-MM-DD`) TC set for this item. */
  date: string;
  /** Already satisfied — acceptance once accepted; the other rows are display-only. */
  done: boolean;
  /**
   * Whether the club can act on this row inline now. Since deposit authorisation is captured at
   * accept time (TW UAT round 1), no checklist row carries an inline action any more — acceptance is
   * met by the accept action. Always false; kept for shape stability with existing callers.
   */
  confirmable: boolean;
}

/**
 * The club's deadline checklist for a proposal: the applicable standard deadlines TC actually set (a
 * toggled-off deadline has a null column and is omitted) plus every custom deadline, sorted soonest
 * date first so the timeline reads chronologically. Pure + deterministic so the web timeline and its
 * tests share one definition (web has no test runner). A row reads as done from its evidence (see
 * {@link ProposalChecklistContext}): acceptance once accepted, the passenger-list row once the
 * manifest is submitted, and every row on a completed (closed-out) trip.
 */
export function proposalChecklist(
  proposal: ProposalChecklistSource,
  transport: DeadlineTransport | null | undefined,
  context: ProposalChecklistContext = {},
): ProposalChecklistItem[] {
  const accepted = proposal.acceptedAt != null;
  const { manifestSubmitted = false, tripCompleted = false } = context;
  // A completed trip is a closed-out record: every deadline reads as met. Otherwise each standard
  // row is done from its own evidence; custom rows have no per-row evidence (done only once closed).
  const standardDone = (key: ProposalDeadlineKey): boolean => {
    if (tripCompleted) return true;
    if (key === "acceptanceDeadline") return accepted;
    if (key === "passengerListDeadline") return manifestSubmitted;
    return false;
  };
  const items: ProposalChecklistItem[] = [];
  for (const def of applicableDeadlines(transport)) {
    const date = proposal[def.key];
    if (date == null) continue; // TC toggled this deadline off — nothing to show
    items.push({
      key: def.key,
      labelKey: def.labelKey,
      label: null,
      date,
      done: standardDone(def.key),
      confirmable: false,
    });
  }
  for (const custom of proposal.customDeadlines ?? []) {
    items.push({
      key: `custom:${custom.id}`,
      labelKey: null,
      label: custom.label,
      date: custom.date,
      done: tripCompleted,
      confirmable: false,
    });
  }
  return items.sort((a, b) => a.date.localeCompare(b.date));
}

// ── Automated deadline reminders (TW-3446) ──────────────────────────────────────
//
// When a proposal is sent, a BullMQ delayed job is scheduled for each applicable deadline TC set;
// when it fires the worker re-reads current state and only notifies the club if that deadline is
// *still pending* (architecture §4.1 — never trust the payload). The two pieces of logic the
// schedule + the fire-time re-check share live here as pure functions so they have one definition,
// proven in the API vitest harness (the worker, the proposal service and the tests all agree).

/** The lead time, in days, a reminder fires ahead of each deadline (a single nudge at T-3d). */
export const DEADLINE_REMINDER_LEAD_DAYS = 3;

/** The proposal statuses for which a deadline is "live" — a Draft has not been sent, an Expired
 *  version has been superseded; reminders apply only to a `Sent`/`Accepted` (live) proposal. */
const LIVE_PROPOSAL_STATUSES: readonly ProposalStatus[] = ["Sent", "Accepted"];

/** Trip statuses in which the trip has left the live workflow — reminders stop (no nudge). */
const REMINDER_DEAD_TRIP_STATUSES: readonly TripStatus[] = ["Cancelled", "Postponed", "Completed", "Expired"];

/** The `proposal.dl*` i18n label key for a deadline (the row's human name); null if unknown. */
export function proposalDeadlineLabelKey(key: ProposalDeadlineKey): string | null {
  return PROPOSAL_DEADLINE_DEFS.find((d) => d.key === key)?.labelKey ?? null;
}

/** The recorded proposal/trip state a deadline reminder re-checks at fire time (booleans, not dates). */
export interface DeadlineReminderState {
  proposalStatus: ProposalStatus;
  tripStatus: TripStatus;
  /** Whether the club has accepted the proposal (acceptance met). */
  accepted: boolean;
}

/**
 * Whether a deadline on this proposal is still "live" — the proposal is Sent/Accepted (not a Draft
 * or an Expired/superseded version) and the trip is still in the workflow (not
 * cancelled/postponed/completed). The standard and custom reminders both gate on this; the custom
 * deadlines have no per-deadline ack, so this is their whole pending rule.
 */
export function isProposalDeadlineLive(state: DeadlineReminderState): boolean {
  if (!LIVE_PROPOSAL_STATUSES.includes(state.proposalStatus)) return false;
  if (REMINDER_DEAD_TRIP_STATUSES.includes(state.tripStatus)) return false;
  return true;
}

/**
 * Whether a reminder for a standard `key` should still be sent given the current state — the
 * schedule decides which jobs to enqueue and the worker re-checks this before notifying. A deadline
 * is moot when the proposal/trip is no longer live (see {@link isProposalDeadlineLive}). Per-deadline:
 * the acceptance reminder stops once the club accepts; the passenger-list and train-proposal
 * reminders fire while the proposal is live (no per-deadline ack — an honest nudge, not a gate).
 */
export function isDeadlineReminderPending(key: ProposalDeadlineKey, state: DeadlineReminderState): boolean {
  if (!isProposalDeadlineLive(state)) return false;
  switch (key) {
    case "acceptanceDeadline":
      return !state.accepted;
    default:
      return true; // passenger list + train proposal — live nudge while the proposal is live
  }
}

// ── TC deadline radar (TW-3446) ──────────────────────────────────────────────────
//
// The back-office ops dashboard's "at risk" KPI + deadline radar were a travel-date stand-in
// (TW-3440); now they are backed by the real, still-pending proposal deadlines. The API returns one
// item per live, pending deadline across every club; these pure folds turn that into the KPI counts
// and the soonest-first list, with one definition shared by the web surface and its tests.

/** A single pending proposal deadline on the cross-club radar (one row per live, unmet deadline). */
export interface ProposalDeadlineRadarItem {
  proposalId: string;
  tripId: string;
  tripReference: string | null;
  clubId: string;
  clubName: string | null;
  /** A standard {@link ProposalDeadlineKey}, or `custom:<id>` for a custom deadline. */
  deadlineKey: string;
  /** The `proposal.dl*` i18n label key for a standard row; null for a custom row. */
  labelKey: string | null;
  /** The literal, operator-entered label for a custom row (shown verbatim); null for a standard row. */
  label: string | null;
  /** The deadline date (ISO `YYYY-MM-DD`). */
  date: string;
}

/** The radar fold: the items soonest-first plus the at-risk / overdue counts. */
export interface DeadlineRadarSummary {
  /** Pending deadlines, soonest date first. */
  items: ProposalDeadlineRadarItem[];
  /** Deadlines due today through the next {@link DEADLINE_AT_RISK_DAYS} days (not yet overdue). */
  atRisk: number;
  /** Deadlines whose date is already in the past. */
  overdue: number;
}

/** A pending deadline is "at risk" when it falls due within this many days. */
export const DEADLINE_AT_RISK_DAYS = 7;

/**
 * Fold the cross-club pending deadlines into the ops radar: sort soonest-first and count the ones
 * that are overdue (date before today) or at risk (due within {@link DEADLINE_AT_RISK_DAYS}).
 * `nowIso` is passed in rather than read from the clock so the result is deterministic and testable.
 */
export function summarizeDeadlineRadar(
  items: readonly ProposalDeadlineRadarItem[],
  nowIso: string,
): DeadlineRadarSummary {
  const today = startOfDayMs(nowIso);
  const atRiskCutoff = today + DEADLINE_AT_RISK_DAYS * 86_400_000;
  let atRisk = 0;
  let overdue = 0;
  for (const item of items) {
    const due = startOfDayMs(item.date);
    if (due < today) overdue += 1;
    else if (due <= atRiskCutoff) atRisk += 1;
  }
  const sorted = [...items].sort((a, b) => a.date.localeCompare(b.date));
  return { items: sorted, atRisk, overdue };
}

/** Midnight-truncated epoch ms for an ISO date/datetime, for whole-day comparisons. */
function startOfDayMs(iso: string): number {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// ── Save (Draft) schema — replace-the-whole-Draft, like hotel-preferences ───────

/** One line as submitted by the builder. `unitPrice`/`amount` are derived server-side, not sent. */
export const proposalLineInputSchema = z.object({
  /** Display-group key so the club's option list renders grouped; null/omitted for an ungrouped line. */
  category: z.enum(PROPOSAL_CATEGORIES).nullable().optional(),
  description: z.string().trim().min(1, "A description is required").max(300),
  qty: z.number().min(0).max(1_000_000),
  unitCost: z.number().min(0).max(1_000_000),
  marginType: z.enum(MARGIN_TYPES),
  marginValue: z.number().min(0).max(1_000_000),
});
export type ProposalLineInput = z.infer<typeof proposalLineInputSchema>;

/**
 * The whole Draft as saved in one request (PUT /api/trips/:tripId/proposal). The line items, the six
 * adaptive deadlines and the covering message are replaced wholesale (a replace-the-set, like the
 * lines). Accommodation is expressed as line items (TW UAT round 1), so there is no separate hotel to
 * confirm here.
 */
/**
 * One deadline as submitted by the builder: an ISO date (`YYYY-MM-DD`) when enabled, or `null` when
 * toggled off (the column is nullable). Omitting a field is treated the same as `null` server-side.
 * Date-only (not datetime) mirrors the prototype's `<input type="date">`; the server stores it at
 * UTC midnight in the `timestamptz` column.
 */
const deadlineField = z.iso.date().nullable().optional();

/** The three standard adaptive deadlines, replaced wholesale alongside the rest of the Draft. */
export const proposalDeadlinesSchema = z.object({
  acceptanceDeadline: deadlineField,
  passengerListDeadline: deadlineField,
  trainProposalDeadline: deadlineField,
});

/**
 * One custom deadline as submitted by the builder (TW UAT round 2): an operator-entered label and a
 * date. No `id` — the whole custom set is replaced wholesale on each save (delete-then-insert), so
 * the server allocates fresh ids; the DTO returns them for display.
 */
export const customDeadlineInputSchema = z.object({
  label: z.string().trim().min(1, "A label is required").max(120),
  date: z.iso.date(),
});
export type CustomDeadlineInput = z.infer<typeof customDeadlineInputSchema>;

export const saveProposalSchema = z.object({
  message: z.string().trim().max(5000).optional(),
  lines: z.array(proposalLineInputSchema).max(100),
  ...proposalDeadlinesSchema.shape,
  /** Free-form deadlines TC added, replaced wholesale (capped to keep the row a sane size). */
  customDeadlines: z.array(customDeadlineInputSchema).max(20).optional(),
});
export type SaveProposalInput = z.infer<typeof saveProposalSchema>;

// ── DTOs ────────────────────────────────────────────────────────────────────────

/** A proposal line as returned to the client — input economics plus the derived client price. */
export interface ProposalLineItemDto {
  id: string;
  /** Display-group key (a {@link ProposalCategory}) so the club's option list renders grouped, or null. */
  category: ProposalCategory | null;
  description: string;
  qty: number;
  /** TC's net cost per unit. */
  unitCost: number;
  marginType: MarginType;
  marginValue: number;
  /** Derived client price per unit. */
  unitPrice: number;
  /** Derived client line total. */
  amount: number;
  sortOrder: number;
}

/** A proposal (with its line items) as returned to the client. */
export interface ProposalDto {
  id: string;
  tripId: string;
  clubId: string;
  version: number;
  status: ProposalStatus;
  message: string | null;
  /** Σ line amounts (client total) — also derivable from the lines via computeProposalTotals. */
  totalAmount: number | null;
  currency: string;
  /**
   * The three standard adaptive deadlines as ISO dates (`YYYY-MM-DD`), or `null` when not set / not
   * applicable to this trip's transport. `applicableDeadlines(trip.transportModes)` says which rows
   * are shown. Free-form deadlines TC added are carried separately in `customDeadlines`.
   */
  acceptanceDeadline: string | null;
  passengerListDeadline: string | null;
  trainProposalDeadline: string | null;
  /** Free-form deadlines TC added (TW UAT round 2), each a label + date, soonest first. */
  customDeadlines: ProposalCustomDeadlineDto[];
  /** When TC sent this proposal to the club (status Draft→Sent), or null while still a Draft (TW-3445). */
  sentAt: string | null;
  /** When the club accepted (status Sent→Accepted), or null while unaccepted (TW-3445). */
  acceptedAt: string | null;
  /** Legacy manual deposit acknowledgement (TW-3445); superseded by the accept-time authorisation below. */
  depositConfirmedAt: string | null;
  /** When the club authorised TC to pay a deposit (taken with acceptance), or null (TW UAT round 1). */
  depositAuthorizedAt: string | null;
  /** The club user who authorised the deposit, recorded for disputes, or null (TW UAT round 1). */
  depositAuthorizedBy: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** The agreed scope: the lines the club kept (all lines until it accepts and deselects some). */
  lineItems: ProposalLineItemDto[];
  /**
   * The options the club declined at acceptance (TRAV-66) — empty until then. Kept out of `lineItems`
   * (so every total/consumer sees only the agreed scope) and rendered as a read-only "not selected"
   * card for both club and back-office users.
   */
  unselectedLineItems: ProposalLineItemDto[];
}
